import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Sparkles } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const ChronicProgramsContent = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/chronic-programs")
            .then(({ data }) => setItems(data?.items || []))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="chronic-programs-page">
            <header className="mb-8">
                <p className="label-uppercase">Chronic disease programs</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50 sm:text-4xl">
                    Long-term care, made simple
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">
                    Enroll in a program to get tailored reminders, daily measurement targets, and doctor-shareable reports — for you or any family member.
                </p>
            </header>

            {loading ? (
                <div className="grid gap-5 md:grid-cols-2">
                    {[1, 2, 3, 4].map((i) => <div key={i} className="h-56 skeleton rounded-3xl" />)}
                </div>
            ) : (
                <div className="grid gap-5 md:grid-cols-2" data-testid="chronic-grid">
                    {items.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/chronic-programs/${p.slug}`}
                            data-testid={`chronic-program-${p.slug}`}
                            className="group relative overflow-hidden rounded-3xl border border-zinc-100 bg-white p-6 transition hover:border-brand-300 hover:shadow-lg dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-brand-400/40"
                        >
                            <div className={`absolute inset-0 -z-10 bg-gradient-to-br ${p.hero_color || "from-brand-100 to-brand-50"} opacity-50 dark:opacity-10`} />
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-3xl">{p.icon_emoji}</span>
                                        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{p.name}</h2>
                                        {p.critical && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
                                                Critical
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">{p.tagline}</p>
                                </div>
                                <ChevronRight className="h-5 w-5 text-zinc-400 transition group-hover:translate-x-1 group-hover:text-brand-700" />
                            </div>
                            <ul className="mt-4 space-y-1.5">
                                {(p.benefits || []).slice(0, 3).map((b) => (
                                    <li key={b} className="flex items-start gap-2 text-xs text-zinc-700 dark:text-zinc-300">
                                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-700 dark:text-brand-400" /> {b}
                                    </li>
                                ))}
                            </ul>
                            {p.enrollment_count > 0 && (
                                <div className="mt-4 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                                    <Sparkles className="h-3 w-3" /> Enrolled · {p.enrollment_count} profile{p.enrollment_count > 1 ? "s" : ""}
                                </div>
                            )}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

const ChronicPrograms = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <ChronicProgramsContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default ChronicPrograms;
